import './Grid.scss';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { Tooltip } from "@progress/kendo-react-tooltip";

import { Popup } from '@progress/kendo-react-popup';
import { Menu, MenuItem } from '@progress/kendo-react-layout';
import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import React from 'react';
import moment from 'moment'
import _ from 'lodash';

import LoadingPanel from '../LoadingPanel';
import sk from '../res/sk.json';
import withStorage from '../utils/withStorage';
import withMeta from '../utils/withMeta';
import ColumnMenu from './ColumnMenu';
import eventEmitter from '../utils/eventEmitter';
import SelectionMenu from './SelectionMenu';
import ValidationAlert from '../ValidationAlert';
import Toolbar from './Toolbar';
import flags from '../utils/flags';
import { eachDeep, copyToClipboard, removeAccents, fieldFormat, getPrimaryField, isInvalid, buildSaveDiscardText, isDateType, addHistoryFilter, isUserFilterActive } from '../utils/helper';
import { getUser } from '../utils/storage';

import ConfirmDialog from '../dialogs/ConfirmDialog';
import ReportDialog from '../dialogs/ReportDialog';
import FileDialog from '../dialogs/FileDialog';

import DialogFilter from './dialogs/DialogFilter';
import ExtendedDialogFilter from './dialogs/DialogFilterExtended';
import DialogSort from './dialogs/DialogSort';

import { Cell } from './cells/Cell'
import DateFilterCell from './cells/DateFilterCell';
import DropdownFilterCell from './cells/DropdownFilterCell';
import { EditCommandCell } from './cells/EditCommandCell'
import { EditBooleanCell } from './cells/EditBooleanCell'
import { EditBooleanCheckCell } from './cells/EditBooleanCheckCell'
import { EditTextCell } from './cells/EditTextCell'
import { EditNumericCell } from './cells/EditNumericCell'
import { EditDropdownCell } from './cells/EditDropdownCell'
import { EditPickerCell } from './cells/EditPickerCell'
import { EditDateTimeCell } from './cells/EditDateTimeCell'
import { EditDateRangeCell } from './cells/EditDateRangeCell'
import { EditDateRangeInputCell } from './cells/EditDateRangeInputCell';
import { FileCell } from './cells/FileCell'
import { EditPhoneNoCell } from './cells/EditPhoneNoCell'
import { ReadMultiCell } from './cells/ReadMultiCell';

import { MemoRow } from './memo/MemoRow'
import { MemoCell } from './memo/MemoCell'


const SORT = [];
const PAGE = {
  skip: 0,
  take: 5
};
const FILTER = {
  filters: []
};

const ENUM_DEFAULT = '-';
const DATE_FORMATS = ['DD.MM.YYYY', 'D.M.YYYY', 'YYYYMMDD', 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ'];

const FILTER_OPERATORS = {
  text: [
    { text: 'grid.filterContainsOperator', operator: 'contains' },
    { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterStartsWithOperator', operator: 'startswith' },
    { text: 'grid.filterEndsWithOperator', operator: 'endswith' },
    { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
    { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
  ],
  numeric: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterGteOperator', operator: 'gte' },
    { text: 'grid.filterGtOperator', operator: 'gt' },
    { text: 'grid.filterLteOperator', operator: 'lte' },
    { text: 'grid.filterLtOperator', operator: 'lt' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' }
  ],
  date: [
    { text: 'grid.filterEqOperator', operator: 'eq' },
    { text: 'grid.filterNotEqOperator', operator: 'neq' },
    { text: 'grid.filterAfterOrEqualOperator', operator: 'gte' },
    { text: 'grid.filterAfterOperator', operator: 'gt' },
    { text: 'grid.filterBeforeOperator', operator: 'lt' },
    { text: 'grid.filterBeforeOrEqualOperator', operator: 'lte' },
    { text: 'grid.filterIsNullOperator', operator: 'isnull' },
    { text: 'grid.filterIsNotNullOperator', operator: 'isnotnull' },
    { text: 'grid.filterBetweenOperator', operator: 'between' }
  ],
  boolean: [
    { text: 'grid.filterBooleanAll', operator: '' },
    { text: 'grid.filterIsTrue', operator: true },
    { text: 'grid.filterIsFalse', operator: false }
  ]
};

let _timer;
let _lastRowClick;
let _lastRowEvent;
let _lastRowIndex = 0;
let _lastCell = null;
let _refresh, _reselect;
let _contextMenuItem;
let _contextMenuBlurTimeoutRef;
let _contextMenuWrapperRef;
let _preventKey = false;
let _column;

/**
 * @typedef {Object} gridSchema
 * @property {string} field - Unique field identifier
 * @property {string} title - Field name
 * @property {(number|string)} width - Field width, "auto" for field expansion
 * @property {(number|string)} compactWidth - Field width in compact mode, "auto" for field expansion
 * @property {string} filter - Filter type - "numeric", "boolean", "date", "text", "enum", "container", "cmd" - unfilterable, unsortable column
 * @property {func} cell - Own custom cell, function
 * @property {number} order - Order of column in grid
 * @property {bool} disabled - Is column hidden?
 * @property {string} tooltip - Tooltip over the column
 * @property {string} textField - Name of shown field, if type is container
 */

/**
 * Grid Component 
 * @module
 * @param {array} [actions] - Types of actions for toolbar selector of items
 * @param {bool} [allColumns] - All colums 
 * @param {string} [cancelText] - Additional cancel text 
 * @param {bool} [cellSelect] - is Cell selected? 
 * @param {func} [cellRender] - Cell render function
 * @param {string} [className] - Grid class name
 * @param {array} [contextMenu] - Row context menu
 * @param {array} data - Grid data
 * @param {object} [dataItem] - Data item for context menu item
 * @param {string} [deleteText] - Additional delete text 
 * @param {any} [detail] - Specifies a React element that will be cloned and rendered inside the detail rows of the currently expanded items
 * @param {number} [disabled] - Disable for toolbar actions, also needs flags
 * @param {string} [discardText] - Discard text for edited and unsaved entry
 * @param {string} [saveChangesDialogText] - SaveChangesDialog text for edited and unsaved entry
 * @param {bool} [editableCell] - EditableCell
 * @param {func} [enterEdit] - Enter cell edit function
 * @param {func} [exitEdit] - Exit cell edit function
 * @param {object} [filter] - Filter
 * @param {func} [filterCellRender] - Filter-cell render function
 * @param {string} [format] - Custom date format
 * @param {func} [formatData] - Format data function
 * @param {number} [hidden] - Hiding toolbar buttons, also needs flex
 * @param {bool} isLoading - Is loading
 * @param {string} [imgName] - Img name for export
 * @param {object} [initFilter] - Init filter
 * @param {array} [initSort] - Init sort 
 * @param {func} [isInvalid] - Check if data are invalid, function
 * @param {bool} [isPicker] - Is picker
 * @param {bool} [modified] - Are data modified?
 * @param {bool} [multiple] - Able to select multiple rows at once
 * @param {func} [onAbortChanges] - On abort changes function
 * @param {func} [onAbortChangesAll] - On abort changes all function
 * @param {func} [onAdd] - Function invoked on entry add
 * @param {func} [onActionSelect] - On action select function
 * @param {func} [onCancel] - Function invoked on entry cancel
 * @param {func} onChange - On change function
 * @param {func} [onConfirm] - On confirm function
 * @param {func} [onContextMenuSelect] - On context menu select function
 * @param {func} [onCopy] - Function invoked on entry copy
 * @param {func} [onCreate] - On create function only when editablegrid is required
 * @param {func} [onDelete] - Function invoked on entry delete
 * @param {func} [onExpandChange] - On expand change function
 * @param {func} [onFilter] - On filter function
 * @param {func} [onGetSchema] - On get schema function
 * @param {func} [onPaste] - On paste from clipboard function
 * @param {func} [onInitDetail] - Init detail for deep link
 * @param {func} [onRowDoubleClick] - On row double click function
 * @param {func} [onSelect] - On select function
 * @param {func} [onSubmit] - On submit function only when editablegrid is required
 * @param {func} [onUpdate] - On update function
 * @param {func} [onUpdateAll] - On update all function
 * @param {object} [parentProps] - Parent props
 * @param {func} [parseValue] - Parse value function
 * @param {string} primaryField - Primary field
 * @param {bool} [rememberSelection] - Should entries stay checked after going to the next page?
 * @param {func} [rowProps] - Row props function
 * @param {gridSchema[]} schema - Grid schema
 * @param {array} [selected] - Selected entries
 * @param {bool} [showAbort] - Show abort button for async requests
 * @param {object} storage - Storage
 * @param {object} [style] - Styles object
 * @param {object} [tlac] - Metadata object for reports
 * @param {bool} [toolbarHidden] - Should toolbar be hidden?
 * @param {number} total - Defines the total number of data items in all pages
 * @param {string} [filterField] - If filter field is different from primary field
 * @param {bool} [showToolbarButtons] - Show buttons with labels (not icons)  in toolbar
 * @param {string} [customToolbarBreadcrumb] - display custom breadcrumb in toolbar
 * @param {string} [db] - db
 * @param {func} [reducer] - Reducer
 * @param {func} [componentFactory] - ComponentFactory
 */
class GridComponent extends React.Component {
  EditCommandCell;
  EditPickerCell;
  EditDropdownCell;
  EditDateTimeCell;
  EditDateRangeCell;
  EditDateRangeInputCell;
  EditBooleanCell;
  EditTextCell;
  EditNumericCell;
  FileCell;
  EditPhoneNoCell;
  ReadMultiCell;
  alertTimeout;

  constructor(props) {
    super(props);

    this.state = {
      page: PAGE,
      filter: FILTER,
      sort: SORT,
      isOpenDelete: false,
      isOpenCancel: false,
      selected: [],
      selectedAll: [],
      columns: props.schema,
      isOpenDiscard: false,
      isOpenSaveChangesDialog: false,
      isOpenFilter: false,
      isOpenSort: false,
      isOpenReportDialog: false,
      isOpenReset: false,
      isOpenWizard: false,
      filterable: !((props.disabled | props.hidden) & flags.FILTER),
      blob: null,
      fileName: null,
      data: this.props.data || [],
      oldData: this.props.data || [],
      dataInvalid: false,
      invalidFields: [],
      selectedCells: [],
      selectedCellsAll: [],
      contextMenuPopupOpen: false,
      selectedFile: null,
      userFilter: [],
      userFilterExtended: [],
      predefinedFilter: [],
      isSchemaLoading: false,
      fixedFilter: props.fixedFilter,
      fastFilter: FILTER,
      extendedFilter: props.withMeta && !props.isPicker,
      managedUsers: [],
      resetValue: null,
      gridSize: null
    }

    this.DialogFilter = this.state.extendedFilter ? ExtendedDialogFilter : DialogFilter;
    this.columns = [];
    this.visibleColumns = [];

    this.setColumns();

    if (!(props.hidden & flags.EDIT) || !(props.hidden & flags.EDITALL) || !(props.hidden & flags.EDITCELL)) {
      this.EditCommandCell = EditCommandCell({
        edit: this.enterEdit,
        update: this.update,
        abortChanges: this.abortChanges,
        editField: "inEdit",
        disabled: this.props.disabled & flags.EDIT
      });

      this.EditPickerCell = EditPickerCell({
        getProps: this.getParentProps,
        itemChange: this.itemChange,
        getColumns: this.getColumns,
        withMeta: props.withMeta
      })

      this.EditDropdownCell = EditDropdownCell({
        getProps: this.getParentProps,
        itemChange: this.itemChange,
        getColumns: this.getColumns
      })
    }

    this.EditDateTimeCell = EditDateTimeCell({
      getColumns: this.getColumns
    })

    this.EditDateRangeCell = EditDateRangeCell({
      getColumns: this.getColumns
    })

    this.EditDateRangeInputCell = EditDateRangeInputCell({
      getColumns: this.getColumns
    })

    this.EditTextCell = EditTextCell({
      getColumns: this.getColumns
    })

    this.EditNumericCell = EditNumericCell({
      getColumns: this.getColumns
    })

    this.EditBooleanCell = EditBooleanCell({
      getColumns: this.getColumns
    })

    this.FileCell = FileCell({
      onClick: this.handleFileClick
    })

    this.EditPhoneNoCell = EditPhoneNoCell({
      getColumns: this.getColumns
    })

    this.ReadMultiCell = ReadMultiCell({
      getColumns: this.getColumns,
    })

    this.ref = React.createRef();
    this.selected = [];
    this.selectedCells = [];

    window.addEventListener('paste', this.handlePaste);
  }

  getDefaultUserId = async () => {
    if (this.props.db) {
      return await this.props.meta.getUser(this.props.db);
    }
    const user = getUser();
    return user?.I_UZ
  }

  getFilterList = async (iUz = null) => {
    try {
      let res = await this.props.meta.getFilters(this.props.identif || this.props.primaryField, this.state.columns, iUz, this.props.db);
      const userFilter = res.filter(x => x.CanDisabled && x.type !== 5);
      const predefinedFilter = res.filter(x => x.CanDisabled && x.type === 5);
      if (iUz === null || iUz === await this.getDefaultUserId()) {
        this.setState({ userFilter, userFilterExtended: userFilter, predefinedFilter });
      }
      else {
        this.setState({ userFilterExtended: userFilter, predefinedFilter });
      }
      return userFilter;
    }
    catch (error) {
      this.setState({ userFilter: [], userFilterExtended: [], predefinedFilter: [] });
    }
  }

  createFilter = async (data, iUz = null) => {
    try {
      await this.props.meta.createFilter(this.props.identif || this.props.primaryField, data, this.state.columns, iUz, this.props.db);
      const list = await this.getFilterList(iUz);
      return list;
    }
    catch (error) {
      throw error;
    }
  }

  updateFilter = async (id, disabled, type, iUz) => {
    try {
      await this.props.meta.updateFilter(this.props.identif || this.props.primaryField, id, disabled, type, iUz, this.props.db);
      this.getFilterList(iUz);
    }
    catch (error) {
      throw error;
    }
  }

  deleteFilter = async (id, iUz) => {
    try {
      await this.props.meta.deleteFilter(this.props.identif || this.props.primaryField, id, iUz, this.props.db);
      await this.getFilterList(iUz);
    }
    catch (error) {
      throw error;
    }
  }

  getManagedUsers = async () => {
    if (this.props.withMeta) {
      let res = await this.props.meta.getSprav(this.props.db);
      this.setState({ managedUsers: res })
    }
  }

  setColumns = () => {
    this.columns = this.getColumns();
    this.visibleColumns = this.getVisibleColumns();
  }

  getParentProps = () => {
    return this.props;
  }

  getColumns = () => {
    let columns = [];
    eachDeep(this.state.columns, null, (item, parent) => {
      if (item.filter !== 'group' && item.field && (!parent || !['container', 'enum', 'radio', 'formdatatable'].includes(parent.filter))) {
        columns.push(item);
      }
    });
    return columns;
  }

  getField = (col) => {
    return col.field +
      (col.filter === "container" ? "." + col.textField : "");
  }

  getVisibleColumns = () => {
    return _.sortBy(this.columns
      .filter(x => !x.disabled && !x.hidden)
      .map(col => ({ ...col, field: this.getField(col) }))
      , x => { return x.order ? x.order : 0 });
  }

  getColOffset = () => {
    return (this.props.hidden & flags.COLUMN_MENU ? 0 : 1) + (this.props.multiple && (!(this.props.hidden & flags.COLUMN_SELECTED)) ? 1 : 0) + (this.props.detail ? 1 : 0);
  }

  getCurrentCell = () => {
    return this.state.selectedCells[this.state.selectedCells.length - 1];
  }

  getChecked = (selected) => {
    if (!selected.length) {
      return false
    }
    else if (this.props.data && this.props.data.length === selected.length) {
      return true;
    }
    else {
      return null;
    }
  }

  handleOpenWizard = () => {
    this.setState({ isOpenWizard: true });
  }

  handleCloseWizard = () => {
    this.setState({ isOpenWizard: false });
  }

  handleOpenSort = () => {
    this.setState({ isOpenSort: true });
  }

  handleCloseSort = () => {
    this.setState({ isOpenSort: false });
  }

  handleConfirmSort = (sort) => {
    this.handleCloseSort();
    this.onSortChange({ sort })
  }

  handleOpenFilter = () => {
    this.setState({ isOpenFilter: true });
  }

  handleCloseFilter = () => {
    this.setState({ isOpenFilter: false });
  }

  handleConfirmFilter = (filter, toggle) => {
    this.handleCloseFilter();
    this.onFilterChange({ filter }, false, toggle)
  }

  handleOpenDelete = () => {
    this.setState({ isOpenDelete: true });
  }

  handleCloseDelete = () => {
    this.setState({ isOpenDelete: false });
  }

  handleOpenCancel = () => {
    this.setState({ isOpenCancel: true });
  }

  handleCloseCancel = () => {
    this.setState({ isOpenCancel: false });
  }

  handleOpenDiscard = () => {
    this.setState({ isOpenDiscard: true });
  }

  handleCloseDiscard = () => {
    this.setState({ isOpenDiscard: false });
  }

  handleConfirmDiscard = () => {
    this.rowClick(_lastRowEvent);
    this.handleCloseDiscard();
  }

  handleOpenSaveChangesDialog = () => {
    this.setState({ isOpenSaveChangesDialog: true });
  }

  handleCloseSaveChangesDialog = () => {
    this.setState({ isOpenSaveChangesDialog: false });
  }

  handleCloseFile = () => {
    this.setState({ selectedFile: null });
  }

  handleCancelSaveChangesDialog = () => {
    this.rowClick(_lastRowEvent);
    this.handleCloseSaveChangesDialog();
  };

  handleConfirmSaveChangesDialog = () => {
    const { parentProps } = this.props;
    if (parentProps) {
      const { detailData } = parentProps[this.props.primaryField];
      const primaryField = this.getPrimaryField();
      this.props.parentProps.updateData(detailData[primaryField], detailData);
    }
    this.handleCancelSaveChangesDialog();
  }

  handleFileClick = (field, dataItem) => {
    _column = this.state.columns.find(x => x.field === field);
    this.setState({ selectedFile: dataItem });
  }

  handleCloseReportDialog = () => {
    this.setState({ isOpenReportDialog: false });
  }

  handleConfirmDelete = async () => {
    await Promise.all(this.state.selected.map(async (item) => {
      await this.props.onDelete(item);
      return item + 1;
    }));
    this.setState({ selected: [], selectedCells: [], selectedAll: [], selectedCellsAll: [] })
    this.handleCloseDelete();
  }

  handleConfirmCancel = async () => {
    await Promise.all(this.state.selected.map(async (item) => {
      await this.props.onCancel(item);
      return item + 1;
    }));
    this.setState({ selected: [], selectedCells: [], selectedAll: [], selectedCellsAll: [] })
    this.handleCloseCancel();
  }

  handleClickDetail = (item) => {
    this.setState({ isDetailOpen: true });
  }

  handleDetailClose = () => {
    this.setState({ isDetailOpen: false });
  }

  handleOpenReset = (value) => {
    this.setState({ isOpenReset: true, resetValue: value });
  }

  handleCloseReset = () => {
    this.setState({ isOpenReset: false, resetValue: null });
  }

  buildResetText = () => {
    switch (this.state.resetValue) {
      case 3:
        return "Obnoviť predvolené nastavenia zoradenia formulára?";
      case 2:
        return "Obnoviť predvolené nastavenia vzhľadu formulára?";
      case 1:
      default:
        return "Obnoviť predvolené nastavenia formulára?";
    };
  }

  handleConfirmReset = () => {
    this.handleReset();
    this.handleCloseReset();
  }

  handleChangePage = (page) => {
    this.setState({ page });
    const filter = this.props.filter || this.state.filter;
    this.props.onChange(this.processFilter(filter, this.state.fixedFilter), this.state.sort, page);
  }

  handleChangeFilterable = (filterable) => {
    if (!((this.props.disabled | this.props.hidden) & flags.FILTER) && filterable.filterable !== null) {
      this.setState({ filterable: filterable.filterable });
    }
  }

  clearSelected = () => {
    this.setState({ selected: [], selectedCells: [], selectedAll: [], selectedCellsAll: [] });
    this.props.onSelect([], false, this.state.selectedAll, []);
  }

  getPropsPage = () => {
    return this.props.page || this.state.page;
  }

  prepareFilter = (filter, event) => {
    const item = event.nativeEvent && filter.filters[0];
    const enumCol = this.columns.find(col => item && col.field === item.field && col.filter === "enum" && col.primaryField !== "I_HOD");
    const filters = enumCol ? [item, ...filter.filters.filter(x => x.field !== enumCol.field + "." + enumCol.primaryField && x !== item)] : filter.filters;

    return {
      ...filter,
      filters: filters
        .map(x => {
          if (x.frmPod) {
            const frm = this.props.parentProps.frms.find(f => f.IFrm === x.frmPod);
            x.childField = frm.ChildField;
            x.parentField = frm.ParentField;
          }
          const col = this.columns.find(col => col.field === x.field);
          if (col && col.filter === "enum" && col.primaryField !== "I_HOD") {
            x.field = x.field + "." + col.primaryField;
          }

          if (typeof x.value === 'string') {
            if (x.value.startsWith('=')) {
              x.operator = 'eq';
            }
            else if (x.value.startsWith('?')) {
              x.operator = 'isnull';
            }
            else if (x.value.startsWith('!?')) {
              x.operator = 'isnotnull';
            }
            else if (x.value.startsWith('*') || x.value.startsWith('%')) {
              x.operator = 'contains';
            }
            else if (x.value.startsWith('!*') || x.value.startsWith('!%')) {
              x.operator = 'doesnotcontain';
            }
            else if (x.value.startsWith('!=') || x.value.startsWith('!')) {
              x.operator = 'neq';
            }
          }

          return x;
        })
    };
  }

  onFilterChange = async (event, timeout = true, toggle) => {
    let filter = event.filter ? this.prepareFilter(event.filter, event) : FILTER;
    let fastFilter = this.state.fastFilter;

    let _page = this.getPropsPage();
    const page = { ..._page, skip: 0 };

    if (this.state.extendedFilter && event.nativeEvent) {
      fastFilter = filter;
      filter = this.state.filter;
      this.setState({ fastFilter, page });
    }
    else {
      const groups = _.groupBy(filter.filters, 'filterId');

      let sysFilters = this.state.filter.filters.filter(x => x.filterType === 6);
      let filters = this.state.filter.filters.filter(x => x.filterType !== 6 && filter.filters.length > 0);
      let disable = toggle;

      _.keys(groups).map(filterId => {
        disable = disable && isUserFilterActive(this.state.filter.filters, { filters: groups[filterId] });
        sysFilters = sysFilters.filter(x => x.filterId !== parseInt(filterId));
        filters = filters.filter(x => x.filterId !== parseInt(filterId) && filter.type === 6);
        return filterId;
      })

      filter.filters = disable ? [...sysFilters, ...filters] : [...sysFilters, ...filters, ...filter.filters];

      this.setState({ filter, page });
    }

    if (this.state.extendedFilter) {
      addHistoryFilter({ ...filter, filters: [...filter.filters, ...fastFilter.filters] }, this.props.primaryField, event.nativeEvent);
    }

    await this.prepareRememberSelection();

    if (this.props.onFilter) {
      this.props.onFilter(filter);
    }

    if (timeout) {
      if (_timer) {
        clearTimeout(_timer);
      }
      _timer = setTimeout(() => {
        this.props.onChange(this.processFilter(filter, this.state.fixedFilter), this.state.sort, page);
      }, 500);
    }
    else {
      this.props.onChange(this.processFilter(filter, this.state.fixedFilter), this.state.sort, page);
    }
  }

  onSortChange = (event) => {
    if (!((this.props.disabled | this.props.hidden) & flags.SORT)) {
      const sort = event.sort;
      const filter = this.props.filter || this.state.filter;
      this.prepareRememberSelection();
      this.props.onChange(this.processFilter(filter, this.state.fixedFilter), sort, this.getPropsPage());
      this.setState({ sort });
      this.applyGlobalization();
      this.updateSort(sort);
    }
  }

  onPageChange = (event) => {
    const page = event.page;
    const filter = this.props.filter || this.state.filter;
    this.prepareRememberSelection();

    if (this.state.page.take !== page.take) {
      this.props.storage.setPage(page);
    }
    this.setState({ page });
    if (_timer) {
      clearTimeout(_timer);
    }
    _timer = setTimeout(() => {
      this.props.onChange(this.processFilter(filter, this.state.fixedFilter), this.state.sort, page);
    }, 200);
  }

  prepareRememberSelection = async () => {
    if (this.props.rememberSelection) {
      const primaryField = this.getPrimaryField();
      let currentPageIds = this.props.data.map(item => item[primaryField]);
      let selectedAll = this.state.selectedAll.filter(item => currentPageIds.indexOf(item[primaryField]) === -1);
      let selectedCellsAll = this.state.selectedCellsAll.filter(item => currentPageIds.indexOf(item[primaryField]) === -1);
      selectedAll = _.unionBy(this.state.selected, this.state.selectedAll, primaryField);
      selectedCellsAll = _.unionWith(this.state.selectedCells, this.state.selectedCellsAll, _.isEqual);
      this.setState({ selected: [], selectedCells: [], selectedAll, selectedCellsAll });
      await this.props.onSelect([], false, selectedAll, []);
      _reselect = true;
    }
    else {
      this.setState({ selected: [], selectedCells: [] });
      await this.props.onSelect([], false, [], []);
    }
  }

  onAdd = (event) => {
    this.clearSelected();
    this.props.onAdd(event);
  }

  onClear = () => {
    this.setState({ fastFilter: FILTER, filter: FILTER }, () => {
      this.onFilterChange({ filter: FILTER }, false);
    });
  }

  onSortClear = () => {
    this.onSortChange({ sort: SORT });
  }

  selectionChange = (event) => {
    const primaryfield = this.getPrimaryField();
    const select = !event.dataItem.selected;
    const selectedCells = this.getSelectedCells([event.dataItem], select, this.state.selectedCells);
    const selected = select ? [...this.state.selected, event.dataItem] : this.state.selected.filter(x => x[primaryfield] !== event.dataItem[primaryfield]);

    this.setState((state) => {
      this.props.onSelect(selected, false, state.selectedAll, selectedCells);
      return { selected, selectedCells };
    });
  }

  headerSelectionChange = (event) => {
    const checked = event.syntheticEvent.target.checked;
    const selected = checked && this.state.selected.length === 0 ? this.props.data : [];
    const selectedCells = checked && this.state.selected.length === 0 ? this.getSelectedCells(this.props.data, true, []) : [];
    this.props.onSelect(selected, false, this.state.selectedAll, selectedCells);
    this.setState({ selected, selectedCells });
  }

  buttonSelection = (action) => {
    switch (action) {
      case 0:
        const selectedCells = this.getSelectedCells(this.props.data, true, []);
        this.setState({ selected: this.props.data, selectedCells: selectedCells });
        this.props.onSelect(this.props.data, false, this.state.selectedAll, selectedCells);
        break;
      case 1:
        this.setState({ selected: [], selectedCells: [] });
        this.props.onSelect([], false, this.state.selectedAll, []);
        break;
      default:
        break;
    }
  }

  tryRowClick = (event) => {
    const primaryField = this.getPrimaryField();

    if (this.props.modified && !this.state.selected.find(x => x[primaryField] === event.dataItem[primaryField])) {
      _lastRowEvent = event;
      if (this.props.parentProps && this.props.parentProps[this.props.primaryField]) {
        this.handleOpenSaveChangesDialog();
      } else {
        this.handleOpenDiscard();
      }
    }
    else {
      this.rowClick(event);
    }
  }

  rowClick = (event) => {
    let selected = [];
    const primaryField = this.getPrimaryField();
    if (this.props.multiple) {
      const select = undefined === this.state.selected.find(x => x[primaryField] === event.dataItem[primaryField]);
      const current = this.state.data.findIndex(x => x[primaryField] === event.dataItem[primaryField]);

      if (event.nativeEvent && !event.nativeEvent.shiftKey) {
        _lastRowIndex = current;
      }

      if (event.nativeEvent && event.nativeEvent.ctrlKey) {
        const deselect = event.selectedCells.filter(x => x.rowIndex === current).length === 0;
        selected = select ? [...this.state.selected, event.dataItem] :
          (deselect ? this.state.selected.filter(x => x[primaryField] !== event.dataItem[primaryField]) : this.state.selected);
      }
      else {
        selected = this.state.data.filter((x, index) => index >= Math.min(_lastRowIndex, current) && index <= Math.max(_lastRowIndex, current));
      }
    }
    else {
      selected = [event.dataItem];
    }
    this.onSelect(selected, event.selectedCells);
  }

  onSelect = (selected, selectedCells) => {
    this.props.onSelect(selected, false, this.state.selectedAll, selectedCells);
    this.setState({ selected, selectedCells });
  }

  onCopy = () => {
    this.setState({ selected: [], selectedCells: [] });
    this.props.onSelect([], true, this.state.selectedAll, []);
    this.props.onCopy();
  }

  buildWidth = (x) => {
    if (!this.state.filterable && x.compactWidth) {
      return x.compactWidth === 'auto' ? undefined : x.compactWidth;
    }
    if (x.width) {
      return x.width === 'auto' ? undefined : x.width;
    }

    switch (x.filter) {
      case 'numeric':
      case 'unitnumeric':
        return this.state.filterable ? 130 : 80;
      case 'boolean':
        return this.state.filterable ? 100 : 70;
      case 'cmd':
        return this.state.filterable ? 100 : 70;
      case 'date':
      case 'datetime':
      case 'dateinput':
      case 'timeinput':
      case 'datetimeinput':
      case '18':
      case '19':
      case '20':
        return this.state.filterable ? 190 : 105;
      case 'text':
      case 'textarea':
      case 'enum':
      case 'buttongroup':
      case 'radio':
      default:
        return this.state.filterable ? 220 : 105;
    }
  }

  buildMinResizableWidth = (x) => {
    if (x.minResizableWidth) {
      return x.minResizableWidth;
    }

    switch (x.filter) {
      case 'boolean':
        return 40;
      case 'date':
        return 80;
      default:
        return 10;
    }
  }

  updateSort = (sort) => {
    if (!this.props.isPicker) {
      const s = [...sort, ...this.columns.map(col => ({ field: this.getField(col), disabled: true })).filter(col => !sort.find(s => s.field === col.field))];
      this.props.meta.updateSort(this.props.primaryField, s, this.props.db);
    }
  }

  getColumn = (columns, col) => {
    const field = this.getField(col);
    let column = undefined;
    eachDeep(columns, null, (item, parent) => {
      if (item.field && !item.disabled && !item.hidden && item.field === field && !['container', 'enum', 'radio'].includes(parent?.filter)) {
        column = item;
      }
    });
    return column;
  }

  onColumnReorder = event => {
    const columns = _.cloneDeep(this.state.columns);
    eachDeep(columns, null, (item, parent) => {
      const column = this.getColumn(event.columns, item);
      if (column) {
        item.order = column.orderIndex;
      }
    });

    this.setState({ columns }, () => { this.updateSchema(); });
  }

  onColumnResize = event => {
    if (event.end) {
      const columns = _.cloneDeep(this.state.columns);
      eachDeep(columns, null, (item, parent) => {
        const column = this.getColumn(event.columns, item);
        if (column) {
          item.width = column.width;
        }
      });

      this.setState({ columns }, () => { this.updateSchema(); });
    }
  }

  onColumnsSubmit = (columns) => {
    this.setState({ columns }, () => { this.updateSchema(); });
  }

  getPage = async () => {
    let page = await this.props.storage.getPage();
    if (page) {
      page.skip = 0;
      this.setState({ page });
      return page;
    }
    else {
      return PAGE;
    }
  }

  getFilterable = async () => {
    const filterable = await this.props.storage.getFilterable();
    this.handleChangeFilterable({ filterable });
  }

  getGridSize = async () => {
    const gridSize = await this.props.storage.getGridSize();
    if (!_.isEqual(this.state.gridSize, gridSize)) {
      this.setState({ gridSize });
    }
  }

  getSort = async () => {
    const sort = this.props.initSort || await this.props.meta.getSort(this.props.primaryField, this.state.columns, this.props.db);
    this.setState({ sort });
    return sort;
  }

  getSchema = async () => {
    const { onGetSchema } = this.props;

    if (!this.props.isPicker) {
      this.setState({ isSchemaLoading: true });
      const schema = await this.props.meta.getSchema(this.props.primaryField, this.props.db);
      if (this.props.schema.length === 0 || this.props.withMeta) {
        this.setState({ columns: schema?.Grid }, () => {
          this.setColumns();
          if (onGetSchema) {
            this.props.onGetSchema(schema);
          }
        });
      }
      else {
        if (schema && schema.Grid && schema.Grid.length > 0) {
          const columns = this.props.schema.map(column => {
            const c = schema.Grid.find(x => x.field === column.field);
            if (c) {
              column.order = c.order;
              column.disabled = c.disabled || column.disabled;
              column.hidden = c.hidden || column.hidden;
              column.width = c.width || column.width;
            }
            return column;
          })
          this.setState({ columns }, () => { this.setColumns(); });
        }
      }

      this.setState({ isSchemaLoading: false });
    }
  }

  getPrimaryField = () => {
    return getPrimaryField(this.props.primaryField);
  }

  getFilter = async () => {
    if (this.props.filter) {
      return this.processFilter(this.props.filter, this.props.fixedFilter);
    }
    let filter, fixedFilter;
    if (this.props.withMeta) {
      const f = await this.props.meta.getFilters(this.props.identif || this.props.primaryField, this.state.columns, null, this.props.db);
      const initFlt = f.filter(x => !x.disabled && x.CanDisabled);
      if (initFlt?.length === 1) {
        filter = this.props.initFilter || initFlt[0];
      } else {
        filter = this.props.initFilter || {
          filters: initFlt.map(x => x.filters).flat()
        };
      }
      filter.filters = filter.filters.map(x => {
        if (x.frmPod) {
          const frm = this.props.parentProps.frms.find(f => f.IFrm === x.frmPod);
          x.childField = frm.ChildField;
          x.parentField = frm.ParentField;
        }

        return x;
      });
      fixedFilter = { filters: [...(this.props.fixedFilter ? this.props.fixedFilter.filters : []), ...f.filter(x => !x.disabled && !x.CanDisabled).map(x => x.filters).flat()] };
    }
    else {
      filter = this.props.initFilter || await this.props.storage.getFilter(this.props.filterField || this.props.primaryField);
      fixedFilter = this.props.fixedFilter;
    }
    this.setState({ filter, fixedFilter, fastFilter: FILTER });
    return this.processFilter(filter, fixedFilter, FILTER);
  }

  handleChangeSelected = ({ name, selected }) => {
    if (name === this.getPrimaryField()) {
      this.onSelect(selected, []);
    }
  }

  applyGlobalization() {
    setTimeout(() => {
      for (let key in sk.grid.extra) {
        let elementsToTranslate = document.querySelectorAll(key);
        for (let element of elementsToTranslate) {
          if (element.getAttribute('title') === null)
            element.setAttribute('title', sk.grid.extra[key]);
        }
      }
    }, 500);
  }

  getSelected = () => {
    if (this.props.selected && this.props.rememberSelection) {
      _reselect = true;
      const primaryField = this.getPrimaryField();
      const pageIds = this.props.data.map(item => item[primaryField]);
      const selected = this.props.selected.filter(item => pageIds.indexOf(item[primaryField]) !== -1);
      const selectedAll = this.props.selected.filter(item => pageIds.indexOf(item[primaryField]) === -1);
      this.setState({ selected, selectedAll });
    } else {
      this.setState({ selected: [], selectedAll: [], selectedCells: [], selectedCellsAll: [] });
    }
  };

  async init() {
    await this.getFilterList();
    await this.getSchema();
    await this.getFilterable();
    await this.getManagedUsers();
    await this.getGridSize();
    const sort = await this.getSort();
    let page = await this.getPage();
    const f = await this.getFilter();
    this.props.onChange(f, sort, page);
    this.getSelected();
    this.setState({ oldData: _.cloneDeep(this.state.data) });
    if (this.props.onInitDetail) {
      this.props.onInitDetail();
    }
  }

  isEqual = (value1, value2) => {
    const val1 = (value1 && value1.getTime ? value1.getTime() : value1);
    const val2 = (value2 && value2.getTime ? value2.getTime() : value2);
    return _.isEqual(val1, val2);
  }

  isValueChanged = (dataItem, field) => {
    const primaryField = this.getPrimaryField();
    const oldDataItem = this.state.oldData.find(x => x[primaryField] === dataItem[primaryField]);

    for (let j = 0; j < (field ? 1 : this.visibleColumns.length); j++) {
      const f = field ? field : this.visibleColumns[j].field;
      const oldValue = oldDataItem ? _.get(oldDataItem, f) : null;
      const newValue = _.get(dataItem, f);
      if (!this.isEqual(oldValue, newValue)) return true;
    }
    return false;
  }

  isDataChanged = (oldData, newData) => {
    if (!oldData || !newData) {
      return false;
    }

    if (newData.length === oldData.length) {
      for (let i = 0; i < newData.length; i++) {
        for (let j = 0; j < this.columns.length; j++) {
          const field = this.columns[j].field;
          if (!this.isEqual(_.get(oldData[i], field), _.get(newData[i], field))) {
            return true;
          }
        }
      }
    } else return true;
    return false;
  }

  isSchemaChanged = (oldSchema, newSchema) => {
    if (newSchema.length === oldSchema.length) {
      let oldCols = [];
      let newCols = [];
      eachDeep(oldSchema, null, (item) => {
        if (item.filter !== 'group' && item.field) {
          oldCols.push({ field: item.field, disabled: item.disabled, editable: item.editable, cell: item.cell ? true : false });
        }
      });
      eachDeep(newSchema, null, (item) => {
        if (item.filter !== 'group' && item.field) {
          newCols.push({ field: item.field, disabled: item.disabled, editable: item.editable, cell: item.cell ? true : false });
        }
      });
      return !_.isEqual(newCols, oldCols)
    } else return true;
  }

  async componentDidMount() {
    eventEmitter.on('CHANGE_PAGE', this.handleChangePage);
    eventEmitter.on('CHANGE_FILTERABLE', this.handleChangeFilterable);
    eventEmitter.on('CHANGE_SELECTED', this.handleChangeSelected);

    await this.init();
    this.applyGlobalization();
  }

  async componentDidUpdate(prevProps, prevState) {
    const isDataChanged = this.isDataChanged(prevProps.data, this.props.data);
    const isSchemaChanged = this.isSchemaChanged(prevProps.schema, this.props.schema);

    if (isSchemaChanged && this.props.schema.length > 0) {
      this.setState({ columns: this.props.schema }, () => { this.setColumns(); });
    }
    if (prevProps.data?.length !== this.props.data?.length) {
      this.getGridSize();
    }
    if (_refresh || isSchemaChanged || isDataChanged) {
      this.setState({ data: this.props.data, oldData: this.props.oldData || _.cloneDeep(this.props.data) });
      _refresh = false;
    }
    if (prevProps.selected !== this.props.selected) {
      this.setState({ selected: this.props.selected });
    }
    const cell = this.getCurrentCell();
    if (!(this.props.hidden & flags.EDITCELL) && cell && prevState.data && this.state.data && prevState.data[cell.rowIndex] && this.state.data[cell.rowIndex] && prevState.data[cell.rowIndex].inEdit !== this.state.data[cell.rowIndex].inEdit) {
      this.focusCell();
    }
    if (_reselect && isDataChanged) {
      _reselect = false;
      const primaryField = this.getPrimaryField();
      const oldSelectedAll = [...this.state.selectedAll, ...this.state.selected]
      const pageIds = this.props.data.map(item => item[primaryField]);
      const selected = oldSelectedAll.filter(item => pageIds.indexOf(item[primaryField]) !== -1);
      const selectedAll = oldSelectedAll.filter(item => pageIds.indexOf(item[primaryField]) === -1);
      const selectedCells = this.state.selectedCellsAll.filter(item => pageIds.indexOf(item.id) !== -1);
      const selectedCellsAll = this.state.selectedCellsAll.filter(item => pageIds.indexOf(item.id) === -1);
      this.setState({ selected, selectedCells, selectedAll, selectedCellsAll });
    }
    const hasFormChanged = (prevProps.primaryField !== this.props.primaryField || prevProps.db !== this.props.db || !_.isEqual(prevProps.fixedFilter, this.props.fixedFilter))
    if (this.props.withMeta && hasFormChanged) {
      await this.init();
    }

  }

  componentWillUnmount() {
    eventEmitter.removeListener('CHANGE_PAGE', this.handleChangePage);
    eventEmitter.removeListener('CHANGE_FILTERABLE', this.handleChangeFilterable);
    eventEmitter.removeListener('CHANGE_SELECTED', this.handleChangeSelected);
    window.removeEventListener('paste', this.handlePaste);
  }

  handleReset = async () => {
    await this.props.meta.deleteSchema(this.props.primaryField, this.state.resetValue, this.props.db);
    await this.init();
  }

  buildFilter = (x) => {
    switch (x.filter) {
      case 'numeric':
      case 'unitnumeric':
        return 'numeric';
      case 'boolean':
        return 'boolean';
      case 'date':
      case 'datetime':
      case 'dateinput':
      case 'datetimeinput':
      case '18':
      case '20':
        return 'date';
      case 'time':
      case 'timeinput':
      case '19':
      case 'enum':
      case 'buttongroup':
      case 'radio':
      case 'container':
      case 'group':
        return null;
      // case "21":
      // case "daterange":
      //   return "21";
      default:
        return 'text';
    }
  }

  buildCell = (props) => {
    const column = this.getColumn(this.state.columns, { field: props.field });
    return <Cell {...props} column={column} />;
  }

  focusCell = () => {
    const input = this.ref.current.querySelector('td.k-state-selected.k-grid-edit-cell input');
    const td = this.ref.current.querySelector('td.k-state-selected');
    const activeElement = document.activeElement;

    //Auto focus na bunky v editovatelnom gride. #start
    const editableInput = this.ref.current.querySelector('tr.k-master-row.k-selected.k-grid-edit-row input');

    if (editableInput) {
      if (editableInput.type === "text")
        editableInput.focus();
    }
    //Auto focus na bunky v editovatelnom gride. #end

    if (input) {
      if ((activeElement && input !== activeElement) || !activeElement) {
        if (input.type === 'checkbox') {
          input.focus();
        } else {
          input.select();
        }
      }
    } else {
      if (td) {
        td.focus();
      }
    }
  }

  onCellClick = async (event, cellProps) => {
    const { dataItem, field } = cellProps;
    const cell = this.getCurrentCell();

    if (field !== "selected" && field !== "" && !(cell && cell.rowIndex === cellProps.rowIndex && cell.columnIndex === cellProps.columnIndex && !event.shiftKey && !event.ctrlKey && this.props.allColumns)) {
      this.selectCell(event, cellProps);
    }

    if (!(this.props.hidden & flags.EDITCELL) && !event.shiftKey && !event.ctrlKey) {
      await this.enterEdit(dataItem, field);
    }
  }

  onCellDoubleClick = (event, cellProps) => {
    const { dataItem } = cellProps;
    if (!event.ctrlKey && !event.shiftKey && !this.props.onRowDoubleClick && !(flags.DETAIL & this.props.hidden)) {
      if (this.props.onConfirm) {
        if (dataItem.inEdit) return;
        this.props.onConfirm();
      }
    }
  }

  cellRender = (tdElement, cellProps) => {
    const primaryField = this.getPrimaryField();
    const { dataItem, field } = cellProps;
    const splitField = field ? field.split(".")[0] : field;
    const rowIndex = this.state.data.findIndex(x => x[primaryField] === dataItem[primaryField]);
    const colIndex = this.visibleColumns.findIndex(x => x.field === field);
    const columnIndex = this.getColOffset() + colIndex;
    const column = this.visibleColumns[colIndex];

    const additionalProps = (field && splitField === dataItem['inEdit'] && colIndex !== -1) ? {} :
      {
        onClick: async (e) => {
          await this.onCellClick(e, { dataItem, field, rowIndex, columnIndex });
        },
        onDoubleClick: (e) => {
          this.onCellDoubleClick(e, { dataItem, field, rowIndex, columnIndex });
        },
        onTouchStart: (e) => {
          const now = Date.now();
          if (now - _lastRowClick < 500 && now - _lastRowClick > 0) {
            this.onCellDoubleClick(e, { dataItem, field, rowIndex, columnIndex });
          }
          _lastRowClick = now;
        },
        tabIndex: "0"
      };

    var tdProps = { ...tdElement.props, ...additionalProps };
    const selectedCells = this.state.selectedCells;
    const newValue = _.get(dataItem, field);

    tdProps.className = tdProps.className.replace(" k-state-selected", "");
    tdProps.className = tdProps.className.replace(" k-state-changed", "");

    if (column && column.locked && !tdProps.className.match(/k-grid-content-sticky/)) {
      tdProps.className += ' k-grid-content-sticky';
    }
    if (selectedCells.find(item => item.rowIndex === rowIndex && item.columnIndex === columnIndex && this.props.cellSelect)) {
      tdProps.className += " k-state-selected";
    }
    if (dataItem.hasChanged && field && field !== 'selected' && this.isValueChanged(dataItem, field)) {
      tdProps.className += " k-state-changed";
    }

    const td = React.cloneElement(tdElement, { ...tdProps, ...additionalProps }, tdElement.props.children);
    const tdRendered = this.props.cellRender ? this.props.cellRender(td, cellProps) : td;

    return <MemoCell tdElement={tdRendered} value={newValue} field={field} Id={dataItem[primaryField]} />;
  }

  filterCellRender = (element, filterCellProps) => {
    const { field, filterType } = filterCellProps;
    const colIndex = this.visibleColumns.findIndex(x => x.field === field);
    const column = this.visibleColumns[colIndex];

    if (filterType === "numeric" && column && column.format && !column.filterCell) {
      const el = element.props.children.props.children[0];
      const operatorEl = element.props.children.props.children.slice(1);
      const formattedEl = React.cloneElement(el, { ...el.props, format: fieldFormat(column.format) }, el.props.children);
      const formattedElRendered = this.props.filterCellRender ? this.props.filterCellRender(formattedEl, filterCellProps) : formattedEl;
      return (
        <div className={"k-filtercell"}>
          <div className={"k-filtercell-wrapper"}>
            {formattedElRendered}
            {operatorEl}
          </div>
        </div>
      );
    }
    return element;
  }

  selectCell = async (e, cellProps) => {
    const minCol = this.getColOffset();
    const primaryField = this.getPrimaryField();
    let { dataItem, field } = cellProps;
    let selectedCells = this.state.selectedCells;
    const colIndex = this.visibleColumns.findIndex(x => x.field === field);

    const splitField = field ? field.split(".")[0] : field;
    const col = this.columns.find(x => x.field === field) || this.columns.find(x => x.filter === "container" && x.field === splitField);
    const lastEditItem = this.state.data.find(item => item.inEdit !== undefined);

    if (lastEditItem && col && ((lastEditItem.inEdit !== true && lastEditItem.inEdit !== col.field) || lastEditItem[primaryField] !== dataItem[primaryField])) {
      await this.exitEdit(lastEditItem, lastEditItem.inEdit);
    }

    if (!e.nativeEvent.ctrlKey || e.nativeEvent.shiftKey || !this.props.multiple || colIndex === -1) { selectedCells = []; }

    if (colIndex !== -1) {
      let columnIndex = minCol + colIndex;
      let rowIndex = this.state.data.findIndex(x => x[primaryField] === dataItem[primaryField]);
      const id = dataItem[primaryField];
      const cell = { id, field, rowIndex, columnIndex };

      _lastCell = _lastCell || this.getCurrentCell();

      if (e.nativeEvent.shiftKey && this.props.multiple && _lastCell) {
        const minRowIndex = Math.min(rowIndex, _lastCell.rowIndex);
        const maxRowIndex = Math.max(rowIndex, _lastCell.rowIndex);
        const minColIndex = Math.min(columnIndex, _lastCell.columnIndex);
        const maxColIndex = Math.max(columnIndex, _lastCell.columnIndex);
        for (let i = minRowIndex; i <= maxRowIndex; i++) {
          for (let j = minColIndex; j <= maxColIndex; j++) {
            const _i = (rowIndex >= _lastCell.rowIndex) ? i : maxRowIndex - i + minRowIndex;
            const _j = (columnIndex >= _lastCell.columnIndex) ? j : maxColIndex - j + minColIndex;
            const dataItemS = this.props.data[_i];
            const idS = dataItemS[primaryField];
            const fieldS = this.visibleColumns[_j - minCol].field;
            const cellS = { id: idS, field: fieldS, rowIndex: _i, columnIndex: _j };
            selectedCells.push(cellS)
          }
        }
      } else {
        _lastCell = null;
        if (!selectedCells.find(item => item.id === id && item.field === field)) {
          selectedCells = [...selectedCells, cell];
        } else {
          selectedCells = selectedCells.filter(item => item.id !== id || item.field !== field);
        }
      }
    }

    e.dataItem = dataItem;
    e.selectedCells = selectedCells;
    this.tryRowClick(e);
  }

  getSelectedCells = (dataItems, select, oldSelectedCells) => {
    const primaryField = this.getPrimaryField()
    const minCol = this.getColOffset();
    const maxCol = this.visibleColumns.length - 1 + minCol;

    let selectedCells = oldSelectedCells;
    for (let i = 0; i < dataItems.length; i++) {
      const dataItem = dataItems[i];
      const id = dataItem[primaryField];
      const rowIndex = this.state.data.findIndex(item => item[primaryField] === id);

      selectedCells = selectedCells.filter(cell => cell.rowIndex !== rowIndex)
      if (select) {
        for (let j = minCol; j <= maxCol; j++) {
          const field = this.visibleColumns[j - minCol].field;
          const cell = { id: id, field: field, rowIndex: rowIndex, columnIndex: j };
          selectedCells.push(cell);
        }
      }
    }

    return selectedCells;
  }

  enterEdit = async (dataItem, field) => {
    const splitField = field ? field.split(".")[0] : field;
    const col = this.columns.find(x => x.field === field) || this.columns.find(x => x.filter === "container" && x.field === splitField);

    if (field && col && (dataItem.inEdit === col.field || dataItem.inEdit === true)) { return; }

    const editcell = !(this.props.hidden & flags.EDITCELL) && field !== "expanded";
    const editable = col ? col.editable : true;
    const pkField = this.getPrimaryField();

    if ((editcell && editable) || field === undefined) {
      const data = await Promise.all(
        this.state.data.map(async (item) => {
          const edit = this.props.enterEdit ? await this.props.enterEdit(item, field) : true;
          return item[pkField] === dataItem[pkField] && ((editcell && editable && edit) || field === undefined) ?
            { ...item, inEdit: col ? col.field : true } : _.omit(item, ['inEdit']);
        })
      )
      this.setState({ data });
    }
  }

  exitEdit = async (dataItem, field) => {
    const data = this.state.data.map(item => { return _.omit(item, ['inEdit']) });
    this.setState({ data }, () => { if (this.props.exitEdit) { this.props.exitEdit(dataItem, field, data); } });
  }

  expandChange = (event) => {
    const primaryField = this.getPrimaryField();

    this.setState(state => {
      return {
        data: state.data.map(x => {
          if (x[primaryField] === event.dataItem[primaryField]) {
            x.expanded = !x.expanded;
          }
          return x;
        })
      }
    });

    if (this.props.onExpandChange) {
      this.props.onExpandChange(event.dataItem);
    }
  }

  abortChanges = async (dataItem) => {
    if (this.props.onAbortChanges !== undefined) {
      await this.props.onAbortChanges(dataItem);
    }

    const primaryField = this.getPrimaryField();
    const originalItem = { ...this.state.oldData.find(item => item[primaryField] === dataItem[primaryField]) };
    const data = this.state.data.map(item => item[primaryField] === originalItem[primaryField] ? originalItem : item);
    this.setState({ data });
  }

  isInvalid = async (field, dataItem) => {
    if (field.disabled || field.hidden) {
      return false;
    }

    const value = _.get(dataItem, field.field);
    const invalid = this.props.isInvalid ? await this.props.isInvalid(field, dataItem, value) : await isInvalid(field, value, true);

    if (invalid) {
      this.setState({
        invalidFields: [...this.state.invalidFields, `${field.title} - ${(field.validationMessage || "pole je povinné")}`]
      })
    }

    return invalid;
  }

  update = async (dataItem) => {
    const invalid = await this.isInvalidItem(dataItem);
    if (!invalid) {
      delete dataItem.inEdit;
      const primaryField = this.getPrimaryField();

      const filter = this.processFilter(this.state.filter, this.state.fixedFilter);
      const sort = this.state.sort;
      let page = this.getPropsPage();
      if (dataItem[primaryField] === 0) {
        const id = await this.props.onCreate(dataItem);
        if (id) {
          const pageNumber = await this.props.meta.getPageNumber(this.props.primaryField, id, filter, sort, page, this.props.db);
          page = { take: page.take, skip: page.take * pageNumber.Data.Page - page.take };
          this.setState({ page });
        }
      }
      else {
        await this.props.onUpdate(dataItem[primaryField], dataItem);
      }

      const data = this.state.data.map(item => {
        return (
          item[primaryField] === dataItem[primaryField] ? _.omit(item, ['inEdit', 'hasChanged']) : item
        )
      })

      this.setState({ data, oldData: data });

      if (this.props.withMeta) {
        this.props.onChange(filter, sort, page);
      }
    } else {
      this.setState({
        dataInvalid: true
      })
      clearTimeout(this.alertTimeout)
      this.alertTimeout = setTimeout(() => {
        this.setState({
          dataInvalid: false,
          invalidFields: []
        })
      }, 4500)
    }
  }

  itemChange = async (event) => {
    const selectionStart = event.nativeEvent?.target.selectionStart;
    const selectionEnd = event.nativeEvent?.target.selectionEnd;

    const primaryField = this.getPrimaryField();
    const { onItemChange } = this.props;
    const hasChanged = this.isValueChanged({ ...event.dataItem, [event.field]: event.value }) ? true : undefined;

    const data = onItemChange ? await onItemChange({ ...event, hasChanged: hasChanged }, this.state.data) :
      this.state.data.map(item =>
        item[primaryField] === event.dataItem[primaryField] ?
          { ...item, [event.field]: event.value, hasChanged: hasChanged } : item
      );

    if (selectionStart && selectionEnd) {
      event.nativeEvent.target.selectionStart = selectionStart;
      event.nativeEvent.target.selectionEnd = selectionEnd;
    }

    this.setState({ data });
  }

  rowRender = (trElement, props) => {
    const primaryField = this.getPrimaryField();
    const rowIndex = this.state.data.findIndex(x => x[primaryField] === props.dataItem[primaryField]);
    const selectedRowCells = this.state.selectedCells.filter(x => x.rowIndex === rowIndex);

    const rowProps = {
      ...this.props.rowProps && this.props.rowProps(props),
      onContextMenu: (e) => {
        if (this.props.contextMenu && this.props.contextMenu.length > 0) {
          e.preventDefault();
          this.handleContextMenuOpen(e, props);
        }
      }
    }

    let className = trElement.props.className;
    if (rowProps?.className) {
      className += ` ${rowProps.className.trimStart()}`;
      delete rowProps.className;
    }
    const tr = React.cloneElement(trElement, { ...trElement.props, ...rowProps, className: className }, trElement.props.children);

    return <MemoRow
      trElement={tr}
      dataItem={props.dataItem}
      selectedRowCells={selectedRowCells}
      columns={this.state.columns}
      multiple={this.props.multiple}
      hidden={this.props.hidden}
      disabled={this.props.disabled}
      isPicker={this.props.isPicker}
      Id={props.dataItem[primaryField]}
    />;
  }

  // context menu
  handleContextMenuOpen = (e, props) => {
    _contextMenuItem = props.dataItem;
    this.offset = { left: e.clientX, top: e.clientY };
    this.setState({ contextMenuPopupOpen: true });
  }

  contextMenuOnBlurHandler = event => {
    clearTimeout(_contextMenuBlurTimeoutRef);
    _contextMenuBlurTimeoutRef = setTimeout(this.contextMenuOnBlurTimeout);
  }

  contextMenuOnFocusHandler = () => {
    clearTimeout(_contextMenuBlurTimeoutRef);
    _contextMenuBlurTimeoutRef = undefined;
  };

  contextMenuOnBlurTimeout = () => {
    this.setState({ contextMenuPopupOpen: false });

    _contextMenuBlurTimeoutRef = undefined;
  };

  oncontextMenuPopupOpen = () => {
    _contextMenuWrapperRef.querySelector('[tabindex]').focus();
  };

  handleContextMenuOnSelect = (e) => {
    this.setState({ contextMenuPopupOpen: false });
    if (this.props.onContextMenuSelect) {
      this.props.onContextMenuSelect(_contextMenuItem, e.item);
    }
  }
  // end context menu

  handleSwitchFilterable = () => {
    this.setState(prevState => {

      const filterable = !prevState.filterable;
      this.props.storage.setFilterable(filterable);
      return { filterable };
    });
  }

  handleExport = async () => {
    const canvas = await html2canvas(this.ref.current);
    canvas.toBlob((blob) => {
      saveAs(blob, this.props.imgName + ".png");
    });
  }

  updateSchema = () => {
    this.setColumns();
    if (!this.props.isPicker) {
      this.props.meta.updateSchema(this.props.primaryField, { Grid: this.columns }, this.props.db);
    }
  }

  getFormType = (primaryField) => {
    switch (primaryField) {
      case "I_EMAIL":
        return 2;
      case "I_PRILOHA":
        return 3;
      default:
        return 1;
    }
  }

  handlePrint = (index, name) => {
    let filter = this.processFilter(this.props.filter || this.state.filter, this.state.fixedFilter);
    if (index <= 1) {
      const fileType = (index === 0 ? "xlsx" : "pdf");
      if (this.props.noMeta) {
        const customPrintProps = this.props.customPrintProps && this.props.customPrintProps();
        const primaryField = customPrintProps?.primaryField || this.getPrimaryField();
        const sort = customPrintProps?.sort || this.state.sort;
        const selectedIds = this.state.selected.map(item => item[primaryField])?.join(',');
        const formName = window.location.href?.split("/")?.pop();
        const parentId = customPrintProps?.id;
        const formType = this.getFormType(primaryField);
        let finalFilter = customPrintProps?.filter || filter;

        if (selectedIds) {
          finalFilter = { filters: [{ field: primaryField, operator: "in", value: selectedIds }] };
        }

        this.props.meta.postPrintNoMeta(primaryField, finalFilter, sort, fileType, { Grid: this.columns }, formName, formType, parentId)
          .then(res => {
            if (!res) return;
            if (fileType === "pdf") {
              this.setState({ blob: res.blob, fileName: res.fileName, isOpenReportDialog: true });
            } else {
              saveAs(res.blob, res.fileName);
            }
          });
      } else {
        this.props.meta.postPrint(this.props.primaryField, filter, this.state.sort, fileType, { Grid: this.columns }, this.props.db)
          .then(res => {
            if (!res) return;
            if (fileType === "pdf") {
              this.setState({ blob: res.blob, fileName: res.fileName, isOpenReportDialog: true });
            } else {
              saveAs(res.blob, res.fileName);
            }
          });
      }
    }

    if (this.props.onPrint && index > 1) { this.props.onPrint(index, name, filter, this.state.selected); }
  }

  handleTlac = async (tlac, target = null) => {
    try {
      let tl = {
        ...tlac,
        params: {
          ...tlac.params,
          [this.props.primaryField]: this.state.selected.map(item => item[this.props.primaryField])?.join(',')
        }
      };
      if (tlac.iSposList?.includes(2)) {
        tl.params[this.props.primaryField] = this.state.data.map(item => item[this.props.primaryField])?.join(',')
      }
      if (this.props.onPrint) {
        await this.props.onPrint(0, tlac.text, tl);
      }
      delete tl.dialogState;
      let res = await this.props.meta.postTlac(this.props.primaryField, tl, target);
      if (res) {
        saveAs(res.blob, res.fileName);
        if (target === "database") await this.props.onChange(this.state.filter, this.state.sort, this.state.page);
      }
    } catch (error) { }
  }

  buildFormat = (x) => {
    if (isDateType(x.filter)) {
      return x.format ? x.format : (x.filter === 'time' ? "{0:HH:mm}" : "{0:dd. MM. yy}")
    }
    else if (x.filter === 'numeric' || x.filter === 'unitnumeric') {
      return x.format ? x.format : "{0:n}"
    }
    else return null;
  }

  handleEditAll = () => {
    this.setState(prevstate => {
      return {
        data: prevstate.data.map(item => (
          { ...item, inEdit: true }
        ))
      }
    });
  }

  /*
  Pridanie novej položky v gride po stlačení tlačidla 
  (nie ikony) uložiť zmeny v toolbare gridu
  */
  submitNewItem = async () => {
    const { onCreate, onSubmit } = this.props;
    const newItem = this.state.data.find(x => x[this.getPrimaryField()] === 0);

    if (newItem != null) {
      await onCreate(newItem);
    }

    onSubmit();
  }

  isInvalidItem = async (dataItem) => {
    return (await Promise.all(
      this.columns.map(async field => await this.isInvalid(field, dataItem))
    )).some(x => x);
  }

  handleUpdateAll = async () => {
    let dataToupdate = [];

    const lastEditItem = this.state.data.find(item => item.inEdit !== undefined);
    if (lastEditItem) {
      await this.exitEdit(lastEditItem, lastEditItem.inEdit);
    }

    const item = this.state.data.find(x => x[this.getPrimaryField()] === 0);

    const areInvalid = (await Promise.all(
      this.state.data.filter(x => x.hasChanged).map(async dataItem => {
        const invalid = await this.isInvalidItem(dataItem);
        if (!invalid) {
          dataToupdate.push(dataItem);
        }
        return invalid;
      })
    )).some(x => x);

    if (areInvalid) {
      this.setState({
        dataInvalid: true
      })
      clearTimeout(this.alertTimeout)
      this.alertTimeout = setTimeout(() => {
        this.setState({
          dataInvalid: false,
          invalidFields: []
        })
      }, 4500)
      return false;
    }
    else {
      if (item) {
        if (this.props.editableCell) {
          this.submitNewItem();
        }
      }
      else if (dataToupdate.length > 0) {

        if (this.props.onUpdateAll) {
          await this.props.onUpdateAll(dataToupdate);
        } else {
          await Promise.all(dataToupdate.map(async (item) => {
            await this.props.onUpdate(item[this.getPrimaryField()], _.omit(item, ['inEdit', 'hasChanged']));
          }));
        }

        const data = this.state.data.map(item => {
          return (
            _.omit(item, ['inEdit', 'hasChanged'])
          )
        })
        this.setState({ data, oldData: data });
      }
      else {
        this.handleAbortChangesAll();
      }
      return true;
    }
  }

  handleAbortChangesAll = () => {
    if (this.props.onAbortChangesAll !== undefined) {
      this.props.onAbortChangesAll();
    }

    const data = this.state.oldData.map(item => {
      return (
        _.omit(item, ['inEdit', 'hasChanged'])
      )
    })
    this.setState({ data });
  }

  handleKeyDown = async (e) => {
    const cell = this.getCurrentCell();
    const containerDiv = this.ref.current.querySelector(":scope > .k-grid > .k-grid-container");
    if (!cell || e.target.closest(".k-grid-container") !== containerDiv) {
      return;
    }

    if (_preventKey) {
      e.preventDefault();
      return;
    }

    _preventKey = true;
    setTimeout(() => { _preventKey = false; }, 100);

    let rowIndex = cell.rowIndex;
    let columnIndex = cell.columnIndex;
    let dataItem = this.state.data[rowIndex];
    let field = cell.field;
    const inEdit = dataItem["inEdit"];

    if (!(this.props.hidden & flags.EDITCELL)) {
      if (["Enter", "Tab"].includes(e.key)) {
        if (!inEdit) {
          this.enterEdit(dataItem, field);
        }
        else {
          if (!(e.target.ariaExpanded === "true")) {
            await this.exitEdit(dataItem, field);
          }
        }
      }
      if (e.key === "Escape" && inEdit) {
        this.abortChanges(dataItem);
        await this.exitEdit(dataItem, field);
      }
      if (e.key === "Delete" && !inEdit) {
        const columns = this.visibleColumns;
        let data = _.cloneDeep(this.state.data);
        const editcell = !(this.props.hidden & flags.EDITCELL);

        this.state.selectedCells.forEach(cell => {
          const columnIndex = columns.findIndex(col => col.field === cell.field);
          const dataItem = data[cell.rowIndex];
          const column = columns[columnIndex];
          const edit = this.props.enterEdit ? this.props.enterEdit(dataItem, column.field) : true;
          const hasChanged = this.isValueChanged({ ...dataItem, [column.field]: null }) ? true : undefined;

          if (dataItem && column && column.editable && editcell && edit) {
            if (dataItem[column.field] !== undefined) {
              data[cell.rowIndex][column.field] = null;
              data[cell.rowIndex].hasChanged = hasChanged;
            }
          }
        });

        this.setState({ data });
      }
    }

    //ctrl+c a ctrl+alt+c
    if (e.keyCode === 67 && e.nativeEvent.ctrlKey) {
      const pkField = this.getPrimaryField();
      const selectedCells = this.state.selectedCells;
      const selectedColumns = this.visibleColumns.filter(col => selectedCells.some(cell => cell.field === col.field));
      let data = '';

      if (e.altKey) {
        selectedColumns.forEach(col => {
          data += col.title;
          data += '\t';
        })
        data = data.replace(/\t$/, "") + '\r\n';
      }

      this.formatData(this.state.data, selectedColumns)
        .filter(dataItem => { return selectedCells.some(cell => cell.id === dataItem[pkField]) })
        .forEach(dataItem => {
          selectedColumns.forEach(col => {
            if (selectedCells.some(cell => (cell.id === dataItem[pkField]) && cell.field === col.field)) {
              data += _.get(dataItem, col.field);
            }
            data += '\t';
          })
          data = data.replace(/\t$/, "") + '\r\n';
        });

      copyToClipboard(data);
    }

    //keynav
    let dx = 0;
    let dy = 0;
    if ((!inEdit && !["Enter", "Tab"].includes(e.key)) || (inEdit && ["Enter", "Tab"].includes(e.key))) {
      switch (e.key) {
        case "Tab": dx = e.nativeEvent.shiftKey ? -1 : 1;
          e.preventDefault();
          e.nativeEvent = { ...e.nativeEvent, shiftKey: false };
          break;
        case "Enter": dy = e.nativeEvent.shiftKey ? -1 : 1;
          e.nativeEvent = { ...e.nativeEvent, shiftKey: false };
          break;
        case "ArrowLeft": dx = -1;
          break;
        case "ArrowRight": dx = 1;
          break;
        case "ArrowUp": dy = -1;
          break;
        case "ArrowDown": dy = 1;
          break;
        default: return;
      }
      const minCol = this.getColOffset();
      const maxCol = this.visibleColumns.length - 1 + minCol;

      if (columnIndex + dx < minCol || columnIndex + dx > maxCol) dx = 0;
      if (rowIndex + dy < 0 || rowIndex + dy > this.state.data.length - 1) dy = 0;

      columnIndex += dx;
      rowIndex += dy;
      dataItem = this.state.data[rowIndex];
      field = this.visibleColumns[columnIndex - minCol].field;
      this.selectCell(e, { dataItem, field });

      //custom v/h-scroll for grid
      if (dx !== 0 || dy !== 0) {
        e.preventDefault();
        const div = containerDiv.querySelector(':scope > .k-virtual-content');
        const firstCells = [...div.querySelectorAll('tr.k-master-row')[0].cells];
        const cells = [...div.querySelectorAll('tr.k-master-row')[rowIndex].cells];
        const cellWidth = cells[columnIndex].offsetWidth;
        const cellHeight = cells[columnIndex].offsetHeight;
        const cellOffsetLeft = cells[columnIndex].offsetLeft;
        const cellOffsetTop = cells[columnIndex].offsetTop;
        const firstIndex = firstCells.findIndex(c => !c.className.includes('sticky'));
        const lockedLeftWidth = firstCells[firstIndex].offsetLeft;
        const lockedWidth = firstCells.filter(c => c.className.includes('sticky')).map(c => c.offsetWidth).reduce((a, b) => a + b, 0);
        const unlockedWidth = div.clientWidth - lockedWidth;
        const unlockedLeftWidth = (cellOffsetLeft - div.scrollLeft - lockedLeftWidth) % unlockedWidth;

        if ((unlockedLeftWidth + cellWidth) > unlockedWidth && dx > 0) {
          div.scrollLeft += cellWidth;
        }

        if (unlockedLeftWidth < 0 && dx < 0) {
          div.scrollLeft -= cellWidth;
        }

        if ((cellOffsetTop - div.scrollTop + cellHeight) > div.clientHeight && dy > 0) {
          div.scrollTop += cellHeight;
        }

        if ((cellOffsetTop - div.scrollTop) < 0 && dy < 0) {
          div.scrollTop -= cellHeight;
        }
      }
    }

    if (inEdit && ["Enter", "Tab"].includes(e.key) && (dx !== 0 || dy !== 0)) {
      this.enterEdit(dataItem, field);
    }
  }

  formatData = (data, columns) => {
    return this.props.formatData ? this.props.formatData(data, columns) :
      data.map(item => {
        let newItem = { ...item };
        columns.forEach(col => {
          const value = _.get(item, col.field);
          _.set(newItem, col.field, (value instanceof Date ? moment(value).format('DD.MM.YYYY') : (value ? String(value) : '')));
        });
        return newItem;
      })
  }

  handlePaste = (e) => {
    const types = e.clipboardData.types;
    const clipboardData = e.clipboardData.getData('text/plain');
    const editcell = !(this.props.hidden & flags.EDITCELL);
    if ((types && types.includes("text/plain") && types.length === 1) || (!clipboardData) || (this.state.selectedCells.length === 0) || (!editcell)) {
      return;
    }

    e.preventDefault();

    const rows = clipboardData.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n$/, '').split('\n');
    const columns = this.visibleColumns;
    let data = _.cloneDeep(this.state.data);

    const cell = this.state.selectedCells[0];
    const cellColIndex = columns.findIndex(col => col.field === cell.field);

    for (var i = 0; i < rows.length; i++) {
      const values = rows[i].split('\t');
      for (var j = 0; j < values.length; j++) {
        const rowIndex = cell.rowIndex + i;
        const columnIndex = cellColIndex + j;
        const dataItem = data[rowIndex];
        const column = columns[columnIndex];
        const edit = this.props.enterEdit ? this.props.enterEdit(dataItem, column.field) : true;

        if (dataItem && column && column.editable && editcell && edit) {
          const value = this.parseValue(values[j].replace(/(\r\n|\n|\r)/g, ''), dataItem, column);
          const hasChanged = this.isValueChanged({ ...dataItem, [column.field]: value }) ? true : undefined;

          if (value !== undefined) {
            data[rowIndex][column.field] = value;
            delete data[rowIndex].hasChanged;
            data[rowIndex].hasChanged = hasChanged;
          }
        }
      }
    }
    this.setState({ data }, () => { if (this.props.onPaste) { this.props.onPaste(this.state.data); } });
  }

  parseValue = (textValue, dataItem, column) => {
    if (this.props.parseValue) {
      return this.props.parseValue(textValue, dataItem, column)
    }

    let value = textValue;
    if (value === undefined || value === null) return undefined;

    switch (column.filter) {
      case 'numeric':
      case 'unitnumeric':
        value = value.replace(',', '.');
        value = value.match(/\./) ? parseFloat(value) : parseInt(value);
        return isNaN(value) ? undefined : value;
      case 'date':
      case 'datetime':
      case 'dateinput':
      case 'datetimeinput':
      case '18':
      case '20':
        const m = moment(value, DATE_FORMATS, true);
        return m.isValid() ? m.toDate() : undefined;
      case 'boolean':
        value = removeAccents(value.toLowerCase());
        return new RegExp(/^[ano|yes|1|true]$/).test(value) ? true :
          new RegExp(/^[nie|no|0|false]$/).test(value) ? false : undefined;
      default:
        return value;
    }
  }

  buildText = (id) => {
    const saveChanges = this.props.parentProps && this.props.parentProps[this.props.primaryField];
    return buildSaveDiscardText(id, saveChanges, this.props.saveChangesDialogText, this.props.discardText);
  };

  buildDeleteCancelText = (selected, cancel) => {
    const ids = selected && selected.map(id => ` ${id[this.getPrimaryField()]}`);
    const what = cancel ? "zrušiť" : "zmazať";
    const mainText = selected && selected.length > 1 ? `Určite chcete ${what} záznamy ${ids}?` : `Určite chcete ${what} záznam ${ids}?`;
    const additionalText = cancel ? this.props.cancelText : this.props.deleteText;
    return additionalText ? mainText + " " + additionalText : mainText;
  }

  buildColumns() {
    let cols = [];

    if (!((this.props.hidden & flags.COLUMN_MENU)) && !(this.props.disabled & flags.COLUMN_MENU)) {
      cols.push(<Column
        className='column-menu'
        filterable={false}
        sortable={false}
        key="columnMenu"
        field=""
        resizable={false}
        reorderable={false}
        width="15px"
        locked={true}
        orderIndex={-2}
        columnMenu={props => <ColumnMenu {...props}
          columns={this.state.columns}
          onColumnsSubmit={this.onColumnsSubmit}
          filterable={this.state.filterable}
          onSwitchFilterable={this.handleSwitchFilterable}
        />}
      />);
    }

    if (this.state.columns.length === 0) {
      cols.push(<Column
        filterable={false}
        sortable={false}
        key="empty"
        field=""
        resizable={false}
        reorderable={false}
        locked={true}
      />);
    }

    if (this.props.multiple && (!(this.props.hidden & (flags.COLUMN_SELECTED | flags.ONLY_SELECT)) && !(this.props.disabled & (flags.COLUMN_SELECTED | flags.ONLY_SELECT)))) {
      cols.push(<Column
        filterable={false}
        sortable={false}
        key="selected"
        field={this.props.disabled & flags.COLUMN_SELECTED ? false : "selected"}
        resizable={false}
        reorderable={false}
        width="50px"
        locked={true}
        orderIndex={-1}
        headerCell={props =>
          <SelectionMenu
            {...props}
            actions={this.props.actions}
            disabled={Boolean(this.props.disabled & flags.COLUMN_SELECTED)}
            onHeaderSelectionChange={this.headerSelectionChange}
            checked={this.getChecked(this.state.selected)}
            buttonSelection={this.buttonSelection}
            selectedCount={this.state.selected.length}
          />}
      />);
    }

    eachDeep(this.state.columns, null, (x) => {
      x.subcols = undefined;
    });

    eachDeep(this.state.columns, null, (x, parent) => {
      if (x.field && !x.disabled && !x.hidden && !(parent && parent.filter !== 'group' && parent.schema)) {
        const isActionOrGroup = x.filter === 'cmd' || x.filter === 'group' || x.filter === 'file';
        const buildedFilter = this.buildFilter(x);
        const col = (x.filter === 'group' ?
          <Column
            key={x.field}
            field={x.field}
            title={x.title}
            filter={buildedFilter}
            width={x.width}
            orderIndex={x.order}
            className={x.filter}
            filterable={x.Filterable}
            sortable={x.Sortable}
            editable={false}
            locked={x.locked}
          >
            {x.subcols ? x.subcols : null}
          </Column>
          :
          <Column
            key={x.field}
            field={this.getField(x)}
            title={x.title}
            filter={buildedFilter}
            format={this.buildFormat(x)}
            width={this.buildWidth(x)}
            cell={(x.cell ? this.buildCell :
              (x.filter === 'boolean' && x.format === '{checkbox}') ? EditBooleanCheckCell :
                x.filter === 'boolean' ? this.EditBooleanCell :
                  (x.filter === 'enum' || x.filter === 'buttongroup' || x.filter === 'radio') ? this.EditDropdownCell :
                    x.filter === 'file' ? this.FileCell :
                      x.filter === 'container' ? this.EditPickerCell :
                        (isDateType(x.filter)) ? this.EditDateTimeCell :
                          x.filter === 'text' ? this.EditTextCell :
                            x.filter === 'numeric' ? this.EditNumericCell :
                              x.filter === 'phoneNumber' ? this.EditPhoneNoCell :
                                x.filter === 'daterange' ? this.EditDateRangeCell :
                                  x.filter === 'daterangeinput' ? this.EditDateRangeInputCell :
                                    x.filter === 'multipicker' || x.filter === 'multi' ? this.ReadMultiCell : null)
            }
            filterCell={(x.filter === 'enum' || x.filter === 'buttongroup' || x.filter === 'radio') ? DropdownFilterCell(this.props.parentProps[x.dataField], ENUM_DEFAULT, null, null, x.textField, x.primaryField) :
              (isDateType(x.filter) ? (filterProps) => <DateFilterCell {...{ type: x.type, ...filterProps }}/> : x.filterCell)}
            orderIndex={x.order}
            className={x.filter + (x.required ? ' required' : '') + (x.className ? ' ' + x.className : '')}
            filterable={!isActionOrGroup && x.Filterable} // Je potrebné aby bol zapnutý aj filter zo schémy
            sortable={!isActionOrGroup && x.Sortable} // Je potrebné aby bol zapnutý aj sort zo schémy
            resizable={x.resizable}
            editable={x.editable}
            editor={buildedFilter}
            locked={x.locked}
            headerCell={props =>
              <div className={"k-link" + (x.className ? ' ' + x.className : '')} onClick={typeof props.onClick === 'function' ? props.onClick : (e) => e.preventDefault()}>
                <Tooltip openDelay={0} position="top" anchorElement="target">
                  <span title={x.tooltip}>{props.title}</span>{props.children}
                </Tooltip>
              </div>
            }
            minResizableWidth={this.buildMinResizableWidth(x)}
          />);

        if (!parent) {
          cols.push(col)
        } else {
          if (!parent.subcols) {
            parent.subcols = [];
          }
          parent.subcols.push(col);
        }
      }
    });

    if (!this.props.isPicker && !(this.props.hidden & flags.EDIT)) {
      cols.push(
        <Column
          key="akcie"
          field="akcie"
          cell={this.EditCommandCell}
          filterable={false}
          sortable={false}
          reorderable={false}
          orderIndex={1000000}
          locked={true}
          width="200px"
        />);
    }

    return cols;
  }

  processFilter = (filter, fixedFilter, fastFilter) => {
    if (!fixedFilter) {
      return { filters: [...filter.filters, ...(fastFilter ? fastFilter.filters : this.state.fastFilter.filters)] };
    }

    return { filters: [...filter.filters, ...fixedFilter.filters, ...(fastFilter ? fastFilter.filters : this.state.fastFilter.filters)] };
  }

  handleRefresh = () => {
    _refresh = true;
    this.props.onChange(this.processFilter(this.props.filter || this.state.filter, this.state.fixedFilter), this.state.sort, this.getPropsPage());
  }

  render() {
    const primaryField = this.getPrimaryField();
    const selected = this.props.selected || this.props.rememberSelection ? _.unionBy(this.state.selected, this.state.selectedAll, primaryField) : this.state.selected;
    const data = this.state.columns.length === 0 || !this.state.data ? [] : this.state.data.map(x => ({ ...x, selected: selected.find(e => e[primaryField] === x[primaryField]) !== undefined }));
    const id = selected.length === 1 ? selected[0][primaryField] : null;
    const filter = this.state.extendedFilter ? this.state.fastFilter : this.props.filter || this.state.filter;
    const expandChange = this.props.detail ? this.expandChange : null;
    const page = this.getPropsPage();
    return (
      <div
        ref={this.ref}
        onKeyDown={this.handleKeyDown}
        tabIndex="0"
        className={'rt-grid-wrapped'}
      >
        <ValidationAlert
          dataInvalid={this.state.dataInvalid}
          invalidFields={this.state.invalidFields}
        />
        {this.props.contextMenu &&
          <Popup
            offset={this.offset}
            show={this.state.contextMenuPopupOpen}
            open={this.oncontextMenuPopupOpen}
            popupClass={'popup-content'}
          >
            <div
              onFocus={this.contextMenuOnFocusHandler}
              onBlur={this.contextMenuOnBlurHandler}
              tabIndex={-1}
              ref={el => (_contextMenuWrapperRef = el)}
            >
              <Menu vertical={true} style={{ display: 'inline-block' }} onSelect={this.handleContextMenuOnSelect}>
                {this.props.contextMenu.map((item, index) =>
                  <MenuItem key={index} text={item.text} />
                )}
              </Menu>
            </div>
          </Popup>
        }
        <Grid
          cellRender={this.cellRender}
          filterCellRender={this.filterCellRender}
          className={'rt-grid ' + (this.state.gridSize ? this.state.gridSize.class : 'grid-medium') + ' ' + this.props.className}
          data={data}
          detail={this.props.detail}
          editField="inEdit"
          expandField="expanded"
          filter={filter}
          filterOperators={FILTER_OPERATORS}
          filterable={this.state.filterable}
          onColumnReorder={this.onColumnReorder}
          onColumnResize={this.onColumnResize}
          onExpandChange={expandChange}
          onFilterChange={this.onFilterChange}
          onHeaderSelectionChange={this.headerSelectionChange}
          onItemChange={this.itemChange}
          onPageChange={this.onPageChange}
          onRowDoubleClick={this.props.onRowDoubleClick}
          onSelectionChange={this.selectionChange}
          onSortChange={this.onSortChange}
          pageable={(this.props.disabled | this.props.hidden) & flags.PAGING ? false : {
            info: true,
            type: 'input',
            pageSizes: [5, 10, 25, 50, 100],
            previousNext: true
          }}
          resizable
          reorderable
          rowRender={this.rowRender}
          selectedField='selected'
          skip={page.skip}
          sort={(this.props.disabled | this.props.hidden) & flags.SORT ? null : this.state.sort}
          sortable={{
            allowUnsort: true,
            mode: 'multiple'
          }}
          style={this.props.style}
          take={page.take}
          total={this.props.total}
        >
          {!(this.props.hidden & flags.TOOLBAR) &&
            <GridToolbar>
              <Toolbar
                handleOpenFilter={this.handleOpenFilter}
                handleOpenSort={this.handleOpenSort}
                onAdd={this.onAdd}
                onDelete={this.handleOpenDelete}
                onCopy={this.onCopy}
                onCancel={this.handleOpenCancel}
                disabled={this.props.disabled}
                hidden={this.props.hidden}
                selected={selected}
                onRefresh={this.handleRefresh}
                onClear={this.onClear}
                onSortClear={this.onSortClear}
                toolbarHidden={this.props.toolbarHidden}
                filterLength={this.props.filter?.filters.length || this.state.filter?.filters.length}
                onSwitchFilterable={this.handleSwitchFilterable}
                filterable={this.state.filterable}
                imgName={this.props.imgName}
                onExport={this.handleExport}
                onPrint={this.handlePrint}
                onTlac={this.handleTlac}
                onReset={this.handleOpenReset}
                onEditAll={this.handleEditAll}
                onUpdateAll={this.handleUpdateAll}
                onAbortChangesAll={this.handleAbortChangesAll}
                printItems={this.props.printItems}
                tlac={this.props.tlac}
                showToolbarButtons={this.props.showToolbarButtons}
                customToolbarBreadcrumb={this.props.customToolbarBreadcrumb}
                data={this.state.data}
                onSubmit={this.props.onSubmit}
                frms={this.props.parentProps?.frms || []}
                selectedFrm={this.props.parentProps?.selectedFrm}
                handleChangeFrm={this.props.parentProps?.handleChangeFrm}
                withMeta={this.props.withMeta}
                isPicker={this.props.isPicker}
                userFilterList={this.state.userFilter}
                predefinedFilterList={this.state.predefinedFilter}
                handleConfirmFilter={this.handleConfirmFilter}
                filter={this.props.filter || this.state.filter}
                reducer={this.props.reducer}
                componentFactory={this.props.componentFactory}
              >
                {typeof this.props.toolbarActions === 'function' ? this.props.toolbarActions() : this.props.toolbarActions}
              </Toolbar>
            </GridToolbar>
          }
          {this.buildColumns()}
        </Grid>

        <LoadingPanel isLoading={this.props.isLoading || this.state.isSchemaLoading} showAbort={this.props.showAbort} />
        {this.state.isOpenDelete && <ConfirmDialog
          isOpen={this.state.isOpenDelete}
          onClose={this.handleCloseDelete}
          onConfirm={this.handleConfirmDelete}
          text={this.buildDeleteCancelText(selected, false)}
        />}
        {this.state.isOpenCancel && <ConfirmDialog
          isOpen={this.state.isOpenCancel}
          onClose={this.handleCloseCancel}
          onConfirm={this.handleConfirmCancel}
          text={this.buildDeleteCancelText(selected, true)}
        />}
        {this.state.isOpenDiscard && <ConfirmDialog
          isOpen={this.state.isOpenDiscard}
          onClose={this.handleCloseDiscard}
          onConfirm={this.handleConfirmDiscard}
          text={this.buildText(id)}
          confirmText="Áno"
          cancelText="Nie"
        />}
        {this.state.isOpenSaveChangesDialog && <ConfirmDialog
          isOpen={this.state.isOpenSaveChangesDialog}
          onClose={this.handleCloseSaveChangesDialog}
          onCancel={this.handleCancelSaveChangesDialog}
          onConfirm={this.handleConfirmSaveChangesDialog}
          text={this.buildText(id)}
          confirmText="Uložiť"
          cancelText="Zahodiť zmeny"
        />}
        <ConfirmDialog
          isOpen={this.state.isOpenReset}
          onClose={this.handleCloseReset}
          onConfirm={this.handleConfirmReset}
          text={this.buildResetText()}
        />
        <FileDialog
          isOpen={this.state.selectedFile !== null}
          onClose={this.handleCloseFile}
          dataItem={this.state.selectedFile}
          column={_column}
        />{this.state.isOpenFilter &&
          <this.DialogFilter
            schema={this.columns}
            parentProps={this.props.parentProps}
            isOpen={this.state.isOpenFilter}
            handleCloseFilter={this.handleCloseFilter}
            handleOpenFilter={this.handleOpenFilter}
            handleConfirmFilter={this.handleConfirmFilter}
            filter={this.props.filter || this.state.filter}
            enumDefault={ENUM_DEFAULT}
            primaryField={this.getPrimaryField()}
            userFilter={!Boolean(this.props.hidden & flags.USER_FILTER)}
            userFilterDisabled={Boolean(this.props.disabled & flags.USER_FILTER)}
            userFilterList={this.state.userFilterExtended}
            getFilterList={this.getFilterList}
            createFilter={this.createFilter}
            updateFilter={this.updateFilter}
            deleteFilter={this.deleteFilter}
            getSchema={this.props.meta.getSchema}
            managedUsers={this.state.managedUsers}
            getDefaultUserId={this.getDefaultUserId}
            db={this.props.db}
          />}
        <DialogSort
          isOpen={this.state.isOpenSort}
          handleCloseSort={this.handleCloseSort}
          handleConfirmSort={this.handleConfirmSort}
          sort={this.state.sort}
          schema={this.columns.filter(col => !(col.Sortable === false)).sort(function (a, b) { return a.OrderSort - b.OrderSort })}
        />
        <ReportDialog
          isOpen={this.state.isOpenReportDialog}
          onClose={this.handleCloseReportDialog}
          blob={this.state.blob}
          fileName={this.state.fileName}
        />
      </div>
    );
  }
}

GridComponent.propTypes = {
  // udrzuj v ABECEDNOM poradi !!!!
  actions: PropTypes.array,
  allColumns: PropTypes.bool,
  cancelText: PropTypes.string,
  cellSelect: PropTypes.bool,
  cellRender: PropTypes.func,
  className: PropTypes.string,
  componentFactory: PropTypes.func,
  contextMenu: PropTypes.array,
  customPrintProps: PropTypes.object,
  data: PropTypes.array.isRequired,
  dataItem: PropTypes.object,
  deleteText: PropTypes.string,
  detail: PropTypes.any,
  disabled: PropTypes.number,
  discardText: PropTypes.string, editableCell: PropTypes.bool,
  enterEdit: PropTypes.func,
  exitEdit: PropTypes.func,
  filter: PropTypes.object,
  filterCellRender: PropTypes.func,
  fixedFilter: PropTypes.object,
  page: PropTypes.object,
  format: PropTypes.string,
  formatData: PropTypes.func,
  hidden: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  imgName: PropTypes.string,
  initFilter: PropTypes.object,
  initSort: PropTypes.array,
  isInvalid: PropTypes.func,
  isPicker: PropTypes.bool,
  modified: PropTypes.bool,
  multiple: PropTypes.bool,
  noMeta: PropTypes.bool,
  withMeta: PropTypes.bool,
  oldData: PropTypes.array,
  onAbortChanges: PropTypes.func,
  onAbortChangesAll: PropTypes.func,
  onAdd: PropTypes.func,
  onActionSelect: PropTypes.func,
  onCancel: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onContextMenuSelect: PropTypes.func,
  onCopy: PropTypes.func,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onExpandChange: PropTypes.func,
  onFilter: PropTypes.func,
  onGetSchema: PropTypes.func,
  onInitDetail: PropTypes.func,
  onItemChange: PropTypes.func,
  onPaste: PropTypes.func,
  onPrint: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onSelect: PropTypes.func,
  onSubmit: PropTypes.func,
  onUpdate: PropTypes.func,
  onUpdateAll: PropTypes.func,
  parentProps: PropTypes.object,
  parseValue: PropTypes.func,
  primaryField: PropTypes.string.isRequired,
  printItems: PropTypes.array,
  reducer: PropTypes.func,
  rememberSelection: PropTypes.bool,
  rowProps: PropTypes.func,
  schema: PropTypes.array.isRequired,
  showToolbarButtons: PropTypes.bool,
  customToolbarBreadcrumb: PropTypes.string,
  saveChangesDialogText: PropTypes.string,
  selected: PropTypes.array,
  showAbort: PropTypes.bool,
  storage: PropTypes.object.isRequired,
  style: PropTypes.object,
  /*toolbarActions: PropTypes.element,*/
  tlac: PropTypes.object,
  toolbarHidden: PropTypes.bool,
  total: PropTypes.number.isRequired,
  filterField: PropTypes.string,
  db: PropTypes.string,
};

export default withMeta(withStorage(GridComponent));
